import React, { useEffect, useRef } from "react";
import { Check, X, AlertTriangle, Minus } from "lucide-react";
import { COMPARATIVA } from "@/content";
import Reveal from "@/components/Reveal";

function Mark({ kind }) {
  const base = { flexShrink: 0 };
  if (kind === "check")
    return (
      <Check size={17} strokeWidth={3} style={{ ...base, color: "var(--lima)" }} aria-hidden="true" />
    );
  if (kind === "neutralCheck")
    return (
      <Check size={17} strokeWidth={2.5} style={{ ...base, color: "var(--gris)" }} aria-hidden="true" />
    );
  if (kind === "x")
    return <X size={17} strokeWidth={2.5} style={{ ...base, color: "#A79FAD" }} aria-hidden="true" />;
  if (kind === "warn")
    return (
      <AlertTriangle size={17} strokeWidth={2.5} style={{ ...base, color: "#A79FAD" }} aria-hidden="true" />
    );
  return <Minus size={17} strokeWidth={2.5} style={{ ...base, color: "#C4BDC9" }} aria-hidden="true" />;
}

const CELL = "px-3 py-4 md:px-5 align-top text-[14px] md:text-[15px]";

export default function Comparativa() {
  const scrollRef = useRef(null);

  // En m\u00f3vil, deja la columna de PRUNEX 1 a la vista al entrar en pantalla
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    if (window.innerWidth >= 768) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      className="py-16 md:py-24"
      style={{ background: "var(--blanco)" }}
      data-testid="section-comparativa"
    >
      <div className="container-page">
        <Reveal className="text-center">
          <h2 className="h2-display" style={{ color: "var(--vino)" }}>
            La diferencia,{" "}
            <span style={{ color: "var(--lima)" }}>sin adornos</span>
          </h2>
        </Reveal>

        <Reveal delay={80} className="mt-10">
          <div
            ref={scrollRef}
            className="overflow-x-auto no-scrollbar -mx-5 px-5 md:mx-0 md:px-0"
          >
            <div style={{ minWidth: 680 }} className="relative pt-6">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className={`${CELL} text-left`} style={{ width: "22%" }}>
                      <span className="sr-only">Criterio</span>
                    </th>
                    {COMPARATIVA.columnas.map((c, i) => {
                      const destacada = i === 2;
                      return (
                        <th
                          key={c}
                          className={`${CELL} text-left font-display`}
                          style={{
                            width: "26%",
                            color: destacada ? "var(--vino)" : "var(--gris)",
                            fontSize: destacada ? 19 : 15,
                            background: destacada ? "var(--lima-soft)" : "transparent",
                            borderTop: destacada ? "2px solid var(--lima)" : "none",
                            borderLeft: destacada ? "2px solid var(--lima)" : "none",
                            borderRight: destacada ? "2px solid var(--lima)" : "none",
                            borderTopLeftRadius: destacada ? 20 : 0,
                            borderTopRightRadius: destacada ? 20 : 0,
                            position: "relative",
                            paddingTop: destacada ? 26 : undefined,
                          }}
                        >
                          {destacada && (
                            <span
                              className="absolute rounded-full text-white text-[10px] font-semibold px-3 py-1"
                              style={{
                                top: -12,
                                left: 18,
                                background: "var(--vino)",
                                letterSpacing: "0.1em",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {COMPARATIVA.badgeRecomendado}
                            </span>
                          )}
                          {c}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {COMPARATIVA.filas.map((f, ri) => {
                    const last = ri === COMPARATIVA.filas.length - 1;
                    return (
                      <tr key={f.criterio}>
                        <th
                          scope="row"
                          className={`${CELL} text-left font-semibold`}
                          style={{
                            color: "var(--vino)",
                            borderTop: "1px solid rgba(107,31,58,0.08)",
                          }}
                        >
                          {f.criterio}
                        </th>
                        {[f.a, f.b, f.c].map((cell, ci) => {
                          const destacada = ci === 2;
                          return (
                            <td
                              key={ci}
                              className={CELL}
                              style={{
                                color: destacada ? "var(--carbon)" : "var(--gris)",
                                background: destacada ? "var(--lima-soft)" : "transparent",
                                borderTop: "1px solid rgba(107,31,58,0.08)",
                                borderLeft: destacada ? "2px solid var(--lima)" : "none",
                                borderRight: destacada ? "2px solid var(--lima)" : "none",
                                borderBottom:
                                  destacada && last ? "2px solid var(--lima)" : "none",
                                borderBottomLeftRadius: destacada && last ? 20 : 0,
                                borderBottomRightRadius: destacada && last ? 20 : 0,
                                fontWeight: destacada ? 600 : 400,
                              }}
                            >
                              <span className="flex items-start gap-2">
                                <Mark kind={cell.i} />
                                <span>{cell.t}</span>
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <p
            className="md:hidden mt-3 text-[12px] text-center"
            style={{ color: "var(--gris)" }}
          >
            &larr; Deslizá la tabla para comparar
          </p>
        </Reveal>
      </div>
    </section>
  );
}
